import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { 
  Award, Plus, Trash2, Save, X, Search,
  FileCheck, QrCode, Download, Eye, UserCheck,
  Calendar, CheckCircle, XCircle, RefreshCw,
  FileText, Globe, Sparkles
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function CertificateManagement({ students = [] }) {
  const [templates, setTemplates] = useState([]);
  const [issuedCertificates, setIssuedCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('issue');
  
  // Issue form states
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [certificateForm, setCertificateForm] = useState({
    title: '',
    title_en: '',
    description: '',
    description_en: '',
    category: 'İngilizce Eğitimi',
    issue_date: new Date().toISOString().split('T')[0]
  });
  
  // Template form
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [templateForm, setTemplateForm] = useState({
    title: '',
    title_en: '',
    description: '',
    description_en: '',
    category: 'İngilizce Eğitimi'
  });

  // Predefined certificate titles
  const certificateTitles = {
    "İngilizce Eğitimi": [
      { tr: "Havacılık İngilizcesi A1 Seviye", en: "Aviation English A1 Level" },
      { tr: "Havacılık İngilizcesi A2 Seviye", en: "Aviation English A2 Level" },
      { tr: "Havacılık İngilizcesi B1 Seviye", en: "Aviation English B1 Level" },
      { tr: "Havacılık İngilizcesi B2 Seviye", en: "Aviation English B2 Level" },
      { tr: "ICAO Havacılık İngilizcesi", en: "ICAO Aviation English" },
      { tr: "Kabin Anonsları Eğitimi", en: "Cabin Announcements Training" }
    ],
    "Kabin Memurluğu Eğitimi": [
      { tr: "Temel Kabin Memurluğu Eğitimi", en: "Basic Cabin Crew Training" },
      { tr: "Uçuş Güvenliği Eğitimi", en: "Flight Safety Training" },
      { tr: "Acil Durum Prosedürleri", en: "Emergency Procedures" },
      { tr: "Yolcu Hizmetleri Eğitimi", en: "Passenger Services Training" },
      { tr: "İlk Yardım ve CRM Eğitimi", en: "First Aid and CRM Training" }
    ],
    "Videolu Dersler": [
      { tr: "Video Eğitim Programı Tamamlama", en: "Video Training Program Completion" },
      { tr: "Online Eğitim Başarı Sertifikası", en: "Online Training Achievement Certificate" }
    ],
    "Genel Başarı": [
      { tr: "Üstün Başarı Ödülü", en: "Outstanding Achievement Award" },
      { tr: "Program Tamamlama Sertifikası", en: "Program Completion Certificate" },
      { tr: "Onur Belgesi", en: "Certificate of Honor" }
    ],
    "Özel Sertifika": [
      { tr: "Özel Sertifika", en: "Special Certificate" }
    ]
  };

  const categories = Object.keys(certificateTitles);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const config = { 
        withCredentials: true,
        headers: { 'Authorization': `Bearer ${token}` }
      };
      const [templatesRes, issuedRes] = await Promise.all([
        axios.get(`${API}/admin/certificates/templates`, config),
        axios.get(`${API}/admin/certificates/issued`, config)
      ]);
      setTemplates(templatesRes.data.templates || []);
      setIssuedCertificates(issuedRes.data.certificates || []);
    } catch (error) {
      console.error('Error fetching certificates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTitle = (titleObj) => {
    setCertificateForm(prev => ({
      ...prev,
      title: titleObj.tr,
      title_en: titleObj.en
    }));
  };

  const handleCreateTemplate = async () => {
    if (!templateForm.title) {
      toast.error('Şablon başlığı gerekli');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/certificates/templates`, templateForm, { 
        withCredentials: true,
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Şablon oluşturuldu');
      setShowTemplateForm(false);
      setTemplateForm({ title: '', title_en: '', description: '', description_en: '', category: 'İngilizce Eğitimi' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Şablon oluşturulamadı');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Bu şablonu silmek istediğinize emin misiniz?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/certificates/templates/${templateId}`, { 
        withCredentials: true,
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Şablon silindi');
      fetchData();
    } catch (error) {
      toast.error('Şablon silinemedi');
    }
  };

  const handleIssueCertificate = async () => {
    if (!selectedStudent) {
      toast.error('Lütfen bir öğrenci seçin');
      return;
    }
    if (!certificateForm.title) {
      toast.error('Sertifika başlığı gerekli');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/certificates/issue`, {
        user_id: selectedStudent.id,
        title: certificateForm.title,
        title_en: certificateForm.title_en || `${certificateForm.title} Certificate`,
        description: certificateForm.description || '',
        description_en: certificateForm.description_en || '',
        category: certificateForm.category,
        issue_date: new Date(certificateForm.issue_date).toLocaleDateString('tr-TR')
      }, { 
        withCredentials: true,
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast.success('Sertifika verildi! 🎉');
      setSelectedStudent(null);
      setCertificateForm({
        title: '',
        title_en: '',
        description: '',
        description_en: '',
        category: 'İngilizce Eğitimi',
        issue_date: new Date().toISOString().split('T')[0]
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sertifika verilemedi');
    }
  };

  const handleRevokeCertificate = async (certId) => {
    if (!window.confirm('Bu sertifikayı silmek istediğinize emin misiniz?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/certificates/${certId}`, { 
        withCredentials: true,
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Sertifika silindi');
      fetchData();
    } catch (error) {
      console.error('Sertifika silme hatası:', error);
      toast.error(error.response?.data?.detail || 'Sertifika silinemedi');
    }
  };

  const handleDownloadPDF = async (certId) => {
    try {
      toast.loading('PDF hazırlanıyor...');
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/certificates/${certId}/download`, {
        withCredentials: true,
        responseType: 'blob',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Blob'u indir
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `AirAcademia_Certificate_${certId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.dismiss();
      toast.success('PDF indirildi!');
    } catch (error) {
      toast.dismiss();
      toast.error('PDF indirilemedi');
    }
  };

  // PDF önizleme - yeni sekmede aç
  const handlePreviewPDF = async (certId) => {
    try {
      toast.loading('Önizleme hazırlanıyor...');
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/certificates/${certId}/download`, {
        withCredentials: true,
        responseType: 'blob',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      
      toast.dismiss();
    } catch (error) {
      toast.dismiss();
      toast.error('Önizleme yüklenemedi');
    }
  };

  const filteredStudents = students.filter(s => 
    s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">🏆 Sertifika Yönetimi</h2>
          <p className="text-gray-500 mt-1">İki dilli profesyonel sertifikalar oluşturun</p>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Yenile
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-amber-500 rounded-xl">
              <Award className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-700">{issuedCertificates.length}</p>
              <p className="text-amber-600 text-sm">Verilen Sertifika</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-blue-500 rounded-xl">
              <FileCheck className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-700">{templates.length}</p>
              <p className="text-blue-600 text-sm">Şablon</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-green-500 rounded-xl">
              <UserCheck className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-700">
                {new Set(issuedCertificates.map(c => c.user_id)).size}
              </p>
              <p className="text-green-600 text-sm">Sertifikalı Öğrenci</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b pb-2">
        {[
          { id: 'issue', label: 'Sertifika Ver', icon: Plus },
          { id: 'issued', label: 'Verilen Sertifikalar', icon: Award },
          { id: 'templates', label: 'Şablonlar', icon: FileCheck }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-amber-500 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Issue Certificate Tab */}
      {activeTab === 'issue' && (
        <div className="grid grid-cols-2 gap-6">
          {/* Student Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <span className="bg-amber-100 text-amber-600 w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold">1</span>
                Öğrenci Seç
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="İsim veya email ile ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              <div className="max-h-[300px] overflow-y-auto space-y-2">
                {filteredStudents.slice(0, 20).map(student => (
                  <div
                    key={student.id}
                    onClick={() => setSelectedStudent(student)}
                    className={`p-3 rounded-lg cursor-pointer transition-all ${
                      selectedStudent?.id === student.id
                        ? 'bg-amber-100 border-2 border-amber-500'
                        : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center text-white font-bold">
                        {student.name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{student.name}</p>
                        <p className="text-sm text-gray-500">{student.email}</p>
                      </div>
                      {selectedStudent?.id === student.id && (
                        <CheckCircle className="w-5 h-5 text-amber-500 ml-auto" />
                      )}
                    </div>
                  </div>
                ))}
                {filteredStudents.length === 0 && (
                  <p className="text-center text-gray-500 py-4">Öğrenci bulunamadı</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Certificate Details */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <span className="bg-amber-100 text-amber-600 w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold">2</span>
                Sertifika Bilgileri
                <Globe className="w-4 h-4 text-blue-500 ml-auto" />
                <span className="text-xs text-blue-500 font-normal">İki Dilli</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {selectedStudent && (
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 mb-4">
                  <p className="text-sm text-amber-600">Seçilen Öğrenci:</p>
                  <p className="font-bold text-amber-800">{selectedStudent.name}</p>
                </div>
              )}
              
              {/* Kategori */}
              <div>
                <label className="text-sm font-medium text-gray-700">Kategori</label>
                <select
                  value={certificateForm.category}
                  onChange={(e) => {
                    setCertificateForm(prev => ({ 
                      ...prev, 
                      category: e.target.value,
                      title: '',
                      title_en: ''
                    }));
                  }}
                  className="w-full mt-1 p-2 border rounded-lg"
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              
              {/* Hazır Başlıklar */}
              <div>
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-amber-500" />
                  Hazır Başlık Seç
                </label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {certificateTitles[certificateForm.category]?.map((title, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleSelectTitle(title)}
                      className={`px-3 py-1 text-xs rounded-full transition-all ${
                        certificateForm.title === title.tr
                          ? 'bg-amber-500 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-amber-100'
                      }`}
                    >
                      {title.tr}
                    </button>
                  ))}
                </div>
              </div>

              {/* Türkçe Başlık */}
              <div>
                <label className="text-sm font-medium text-gray-700">
                  🇹🇷 Sertifika Başlığı (Türkçe) *
                </label>
                <Input
                  value={certificateForm.title}
                  onChange={(e) => setCertificateForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Örn: Havacılık İngilizcesi A1 Seviye"
                  className="mt-1"
                />
              </div>
              
              {/* İngilizce Başlık */}
              <div>
                <label className="text-sm font-medium text-gray-700">
                  🇬🇧 Certificate Title (English)
                </label>
                <Input
                  value={certificateForm.title_en}
                  onChange={(e) => setCertificateForm(prev => ({ ...prev, title_en: e.target.value }))}
                  placeholder="e.g., Aviation English A1 Level"
                  className="mt-1"
                />
              </div>
              
              {/* Türkçe Açıklama */}
              <div>
                <label className="text-sm font-medium text-gray-700">
                  🇹🇷 Açıklama (Türkçe) - Opsiyonel
                </label>
                <textarea
                  value={certificateForm.description}
                  onChange={(e) => setCertificateForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Boş bırakırsanız otomatik zengin içerik oluşturulur..."
                  className="w-full mt-1 p-2 border rounded-lg text-sm"
                  rows="2"
                />
              </div>
              
              {/* İngilizce Açıklama */}
              <div>
                <label className="text-sm font-medium text-gray-700">
                  🇬🇧 Description (English) - Optional
                </label>
                <textarea
                  value={certificateForm.description_en}
                  onChange={(e) => setCertificateForm(prev => ({ ...prev, description_en: e.target.value }))}
                  placeholder="Leave empty for auto-generated rich content..."
                  className="w-full mt-1 p-2 border rounded-lg text-sm"
                  rows="2"
                />
              </div>
              
              {/* Tarih */}
              <div>
                <label className="text-sm font-medium text-gray-700">Veriliş Tarihi</label>
                <Input
                  type="date"
                  value={certificateForm.issue_date}
                  onChange={(e) => setCertificateForm(prev => ({ ...prev, issue_date: e.target.value }))}
                  className="mt-1"
                />
              </div>
              
              <Button 
                className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 mt-4"
                onClick={handleIssueCertificate}
                disabled={!selectedStudent || !certificateForm.title}
              >
                <Award className="w-4 h-4 mr-2" />
                Sertifika Ver
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Issued Certificates Tab */}
      {activeTab === 'issued' && (
        <div className="space-y-4">
          {issuedCertificates.length === 0 ? (
            <Card className="bg-gray-50">
              <CardContent className="p-8 text-center">
                <Award className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-medium text-gray-600">Henüz sertifika verilmemiş</h3>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {issuedCertificates.map(cert => (
                <Card key={cert.id} className="hover:shadow-md transition-all">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center">
                          <Award className="w-7 h-7 text-white" />
                        </div>
                        <div>
                          <h3 className="font-bold text-gray-900">{cert.title}</h3>
                          {cert.title_en && (
                            <p className="text-sm text-gray-400 italic">{cert.title_en}</p>
                          )}
                          <p className="text-sm text-gray-500">{cert.user_name} • {cert.user_email}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">
                              {cert.category}
                            </span>
                            <span className="text-xs text-gray-400">
                              <Calendar className="w-3 h-3 inline mr-1" />
                              {cert.issue_date}
                            </span>
                            <span className="text-xs text-gray-400 font-mono">
                              {cert.code}
                            </span>
                            {cert.status === 'active' && (
                              <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                                Aktif
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleDownloadPDF(cert.id)}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        >
                          <Download className="w-4 h-4 mr-1" />
                          PDF
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handlePreviewPDF(cert.id)}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Önizle
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleRevokeCertificate(cert.id)}
                        >
                          <XCircle className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <div className="space-y-4">
          <Button onClick={() => setShowTemplateForm(true)} className="bg-blue-500 hover:bg-blue-600">
            <Plus className="w-4 h-4 mr-2" />
            Yeni Şablon
          </Button>
          
          {templates.length === 0 ? (
            <Card className="bg-gray-50">
              <CardContent className="p-8 text-center">
                <FileCheck className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-medium text-gray-600">Henüz şablon yok</h3>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              {templates.map(template => (
                <Card key={template.id}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-bold text-gray-900">{template.title}</h3>
                        {template.title_en && (
                          <p className="text-sm text-gray-400 italic">{template.title_en}</p>
                        )}
                        <p className="text-sm text-gray-500">{template.description}</p>
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full mt-2 inline-block">
                          {template.category}
                        </span>
                      </div>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        className="text-red-500"
                        onClick={() => handleDeleteTemplate(template.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Template Form Modal */}
      {showTemplateForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Yeni Şablon</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowTemplateForm(false)}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700">🇹🇷 Şablon Adı *</label>
                <Input
                  value={templateForm.title}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Örn: A1 Seviye Tamamlama"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">🇬🇧 Template Name</label>
                <Input
                  value={templateForm.title_en}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, title_en: e.target.value }))}
                  placeholder="e.g., A1 Level Completion"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Kategori</label>
                <select
                  value={templateForm.category}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full mt-1 p-2 border rounded-lg"
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div className="flex gap-2 pt-4">
                <Button variant="outline" className="flex-1" onClick={() => setShowTemplateForm(false)}>
                  İptal
                </Button>
                <Button className="flex-1 bg-blue-500 hover:bg-blue-600" onClick={handleCreateTemplate}>
                  <Save className="w-4 h-4 mr-2" />
                  Kaydet
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

    </div>
  );
}

export default CertificateManagement;
